import { useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import { ProSubscriptionType } from "@lifeat/shared/stripe/StripeSubscriptionTypes";
import { getCurrentPrice } from "@lifeat/shared/stripe/StripeSubscriptions";
import { hasFreeAppAccess } from "@lifeat/shared/user/UserUtil";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import LifeAtToggle from "@common/LifeAtToggle/LifeAtToggle";
import { useUserContext } from "@contexts/UserContext";
import StudentDiscountCode from "@features/upgrade-flow/components/StudentDiscountCode";
import Arrow from "@icons/Arrow-Left-Icon.svg";
import {
    PricingPageHeader,
    PricingTiersContainer,
    PricingToggleContainer,
} from "../../../features/upgrade-flow/components/PricingPageContent";
import { FreeTier, ProTier } from "../../../features/upgrade-flow/components/PricingTiers";
import { isUserFromAd } from "../OnboardingUtil";
import { SplitLayout } from "../SplitLayout";
import { RoiCalculator } from "./RoiCalculator";

const UpgradeCtaContainer = styled.div`
    width: 100%;
    height: 100%;
    padding: 64px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;
const UpgradeCtaContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 640px;
    width: fit-content;
    gap: 15px;
`;
const ContinueButtonContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`;
const ArrowIcon = styled(Arrow)`
    fill: ${(props) => props.theme.textColorPrimary};
    transform: rotate(180deg);
`;

const PRICING_TOGGLE_LABEL_MONTHLY = "Monthly";
const PRICING_TOGGLE_LABEL_YEARLY = "Yearly (Save 15%)";
const SKIP_ONE_TIME_OFFER_LABEL = "Skip one-time offer";

interface OnboardingUpgradeWithoutTrialProps {
    onNext: () => void;
}

export function OnboardingUpgradeWithoutTrial(props: OnboardingUpgradeWithoutTrialProps) {
    const { userData } = useUserContext();
    const [subscriptionPeriod, setSubscriptionPeriod] = useState<ProSubscriptionType>("annual");
    const subscriptionCost = useMemo(() => {
        switch (subscriptionPeriod) {
            case "monthly":
                return getCurrentPrice("monthly");
            case "annual":
                return getCurrentPrice("annual") / 12;
        }
    }, [subscriptionPeriod]);
    const isAppBlocked = isUserFromAd() || !hasFreeAppAccess(userData);
    const handlePeriodToggle = () => {
        if (subscriptionPeriod === "monthly") {
            setSubscriptionPeriod("annual");
        } else if (subscriptionPeriod === "annual") {
            setSubscriptionPeriod("monthly");
        }
    };

    return (
        <SplitLayout
            className="notranslate"
            translate="no"
            left={
                <UpgradeCtaContainer>
                    <UpgradeCtaContent>
                        {!isAppBlocked && <StudentDiscountCode />}
                        <PricingPageHeader>Special offer today only!</PricingPageHeader>
                        <PricingToggleContainer>
                            <LifeAtBodySecondary>{PRICING_TOGGLE_LABEL_MONTHLY}</LifeAtBodySecondary>
                            <LifeAtToggle isEnabled={subscriptionPeriod === "annual"} onChange={handlePeriodToggle} />
                            <LifeAtBodySecondary>{PRICING_TOGGLE_LABEL_YEARLY}</LifeAtBodySecondary>
                        </PricingToggleContainer>

                        <PricingTiersContainer>
                            {!isMobile && !isAppBlocked && <FreeTier />}
                            <ProTier
                                subscriptionPeriod={subscriptionPeriod}
                                amplitudeEventSource={isAppBlocked ? "onboardingBlockedFlow" : "onboarding"}
                                skipTrial
                            />
                        </PricingTiersContainer>
                        <ContinueButtonContainer>
                            <LifeAtButtonV2 onClick={props.onNext} variant={"ghost"}>
                                {SKIP_ONE_TIME_OFFER_LABEL}
                                <ArrowIcon />
                            </LifeAtButtonV2>
                        </ContinueButtonContainer>
                    </UpgradeCtaContent>
                </UpgradeCtaContainer>
            }
            right={<RoiCalculator subscriptionCost={subscriptionCost} />}
        />
    );
}
